import { Fragment } from "react";
import { PART_LABELS, partMeaning, type DesignationPart, type ReadsAsLookup } from "./designationParts";
import { GradeTable } from "./GradeTable";
import { PartChip } from "./PartChip";
import type { DesignationWithGrades } from "./resinGroups";
import type { DesignationView } from "../types";

interface Props {
  designations: DesignationWithGrades[];
  emptyText: string;
  openDesignationIds: Set<number>;
  onToggleDesignation: (designationId: number) => void;
  readsAsByCode: ReadsAsLookup;
  canWrite: boolean;
  onTdsChanged: () => void;
}

const COLUMN_TITLES = ["RS code", "Reads As", "Parts"];
const COUNT_COLUMN_TITLES = ["Grades", "With a TDS"];
const COLUMN_COUNT = COLUMN_TITLES.length + COUNT_COLUMN_TITLES.length + 1;
const HEADER_CELL_CLASSES =
  "bg-rs-black px-3 py-2.5 text-[11.5px] font-medium uppercase tracking-wider text-rs-light-gray border-b border-rs-jet whitespace-nowrap";
const COUNT_CELL_CLASSES = "text-right tabular-nums whitespace-nowrap";

function partHoverLabel(part: DesignationPart, designation: DesignationView, readsAsByCode: ReadsAsLookup): string {
  return `${PART_LABELS[part.slotName]} · ${partMeaning(part, designation.standardCode, readsAsByCode)}`;
}

function countText(count: number) {
  return count ? count : <span className="text-[#a9b0a1]">0</span>;
}

export function ResinTable({
  designations,
  emptyText,
  openDesignationIds,
  onToggleDesignation,
  readsAsByCode,
  canWrite,
  onTdsChanged,
}: Props) {
  return (
    <div className="overflow-x-auto rounded-md border border-rs-jet">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            <th className={`${HEADER_CELL_CLASSES} w-9`}>
              <span className="sr-only">Show grades</span>
            </th>
            {COLUMN_TITLES.map((title) => (
              <th key={title} className={`${HEADER_CELL_CLASSES} text-left`}>
                {title}
              </th>
            ))}
            {COUNT_COLUMN_TITLES.map((title) => (
              <th key={title} className={`${HEADER_CELL_CLASSES} text-right`}>
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {designations.length === 0 && (
            <tr>
              <td colSpan={COLUMN_COUNT} className="px-3 py-3 text-[#a9b0a1]">
                {emptyText}
              </td>
            </tr>
          )}
          {designations.map(({ designation, gradeLinks }) => {
            const sortedParts = [...designation.parts].sort((first, second) => first.partPosition - second.partPosition);
            const isOpen = openDesignationIds.has(designation.designationId);
            const gradesWithTdsCount = gradeLinks.filter((gradeLink) => (gradeLink.tdsFileCount ?? 0) > 0).length;
            const rowCellClasses = `px-3 py-2.25 align-middle border-t ${isOpen ? "bg-rs-jet border-[#3c4235]" : "border-rs-jet"}`;
            return (
              <Fragment key={designation.designationId}>
                <tr
                  className={`cursor-pointer ${isOpen ? "" : "hover:bg-[#1d2119]"}`}
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("[data-part-chip]")) return;
                    onToggleDesignation(designation.designationId);
                  }}
                >
                  <td className={rowCellClasses}>
                    <button
                      type="button"
                      className="bg-transparent border-0 px-0.5 text-[13px] leading-none text-[#a9b0a1] cursor-pointer hover:text-rs-green"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Hide" : "Show"} grades for ${designation.designationCode}`}
                    >
                      {isOpen ? "▾" : "▸"}
                    </button>
                  </td>
                  <td className={rowCellClasses}>
                    <code className="whitespace-nowrap text-[13px] font-medium text-rs-green">{designation.designationCode}</code>
                  </td>
                  <td className={`${rowCellClasses} text-white`}>{designation.readsAs}</td>
                  <td className={rowCellClasses}>
                    <span className="flex flex-wrap gap-1">
                      {sortedParts.map((part) => (
                        <PartChip key={part.partPosition} code={part.code} hoverLabel={partHoverLabel(part, designation, readsAsByCode)} />
                      ))}
                      {designation.suffixText && (
                        <PartChip code={`/${designation.suffixText}`} hoverLabel="Suffix · free text" isSuffix />
                      )}
                    </span>
                  </td>
                  <td className={`${rowCellClasses} ${COUNT_CELL_CLASSES} text-white`}>{countText(gradeLinks.length)}</td>
                  <td className={`${rowCellClasses} ${COUNT_CELL_CLASSES} text-white`}>{countText(gradesWithTdsCount)}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="p-0">
                      <GradeTable
                        designation={designation}
                        gradeLinks={gradeLinks}
                        readsAsByCode={readsAsByCode}
                        canWrite={canWrite}
                        onTdsChanged={onTdsChanged}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
